// src/components/Navbar/Navbar.jsx
import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import FullWhiteLogoSvg from '../../assets/images/logo-v3/Full-White.svg';
import FullColorLogoSvg from '../../assets/images/logo-v3/Full-Light.svg';
import ThemeToggle from '../ui/ThemeToggle';
import { useTheme } from '../../context/ThemeContext';
import navigation from '../../data/navigation';
import '../../assets/css/layouts/Header.scss';

const Header = ({ isScrolled }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const location = useLocation();
    const { theme } = useTheme();

    const headerNavItems = navigation.filter(item => item.showInHeader);
    const navLinkIds = headerNavItems.map(item => item.id);
    const linkRefs = useRef([]);
    const [hoveredLink, setHoveredLink] = useState(null);
    const [indicatorStyle, setIndicatorStyle] = useState({});

    // Update indicator position
    const getCurrentPageLink = () => {
        const path = location.pathname;
        const currentNavItem = headerNavItems.find(item => item.path === path);
        return currentNavItem ? currentNavItem.id : null;
    };

    const updateIndicatorStyle = (linkElement, transition) => {
        const { offsetLeft, offsetWidth } = linkElement;
        setIndicatorStyle({
            left: offsetLeft,
            width: offsetWidth,
            transition,
        });
    };

    const updateIndicatorForDesktop = () => {
        const currentLink = hoveredLink || getCurrentPageLink();
        if (currentLink) {
            const index = navLinkIds.indexOf(currentLink);
            if (index !== -1) {
                const linkElement = linkRefs.current[index];
                if (linkElement) {
                    updateIndicatorStyle(linkElement, 'left 0.3s, width 0.3s');
                }
            } else {
                setIndicatorStyle({});
            }
        } else {
            setIndicatorStyle({});
        }
    };

    const updateIndicatorForMobile = () => {
        const currentLink = getCurrentPageLink();
        if (currentLink) {
            const index = navLinkIds.indexOf(currentLink);
            if (index !== -1) {
                const linkElement = linkRefs.current[index];
                if (linkElement) {
                    updateIndicatorStyle(linkElement, 'none');
                }
            } else {
                setIndicatorStyle({});
            }
        } else {
            setIndicatorStyle({});
        }
    };

    const updateIndicator = () => {
        const breakpoint = 768;
        const isDesktop = window.innerWidth >= breakpoint;
        if (isDesktop) {
            updateIndicatorForDesktop();
        } else {
            updateIndicatorForMobile();
        }
    };

    useEffect(() => {
        updateIndicator();
    }, [hoveredLink, location.pathname]);

    useEffect(() => {
        const handleResize = () => {
            updateIndicator();
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // resize indicator when isScrolled changes
    useEffect(() => {
        updateIndicator();
    }, [isScrolled]);


    // Toggle the full-screen menu
    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    return (
        <nav className={`navbar ${isScrolled ? 'navbar--scrolled' : ''} ${theme === 'dark' ? 'navbar--dark' : 'navbar--light'}`}>
            <div className="navbar__container">
                <div className="navbar__logo">
                    <NavLink to="/" end>
                        {theme === 'light' && isScrolled ? (
                            <FullColorLogoSvg id="nav-logo-color" className="navbar__logo-svg" />
                        ) : (
                            <FullWhiteLogoSvg id="nav-logo-white" className="navbar__logo-svg" />
                        )}
                    </NavLink>
                </div>
                {/* Hamburger Icon for Small Screens */}
                <div
                    className={`navbar__hamburger ${isMenuOpen ? 'open' : ''}`}
                    onClick={toggleMenu}
                    aria-label="Toggle Menu"
                >
                    <span />
                    <span />
                    <span />
                </div>
                {/* Standard Links for Large Screens */}
                <div className="navbar__links-container">
                    <ul className="navbar__links">
                        {headerNavItems.map((navItem, index) => (
                            <li key={navItem.id} ref={(el) => (linkRefs.current[index] = el)}>
                                <NavLink
                                    to={navItem.path}
                                    className={({ isActive }) => (isActive ? 'active' : '')}
                                    aria-current={location.pathname === navItem.path ? 'page' : undefined}
                                    end={navItem.path === '/'}
                                    onMouseEnter={() => setHoveredLink(navItem.id)}
                                    onMouseLeave={() => setHoveredLink(null)}
                                >
                                    {navItem.displayName}
                                </NavLink>
                            </li>
                        ))}
                    </ul>
                    <div className="navbar__indicator" style={indicatorStyle}></div>
                </div>
                {/* Full-Screen Menu Overlay */}
                {isMenuOpen && (
                    <div className="navbar__overlay">
                        <ul className="navbar__overlay-links">
                            {headerNavItems.map((navItem) => (
                                <li key={navItem.id}>
                                    <NavLink
                                        to={navItem.path}
                                        className={({ isActive }) => (isActive ? 'active' : '')}
                                        onClick={() => setIsMenuOpen(false)}
                                        aria-current={location.pathname === navItem.path ? 'page' : undefined}
                                        end={navItem.path === '/'}
                                    >
                                        {navItem.displayName}
                                    </NavLink>
                                </li>
                            ))}
                        </ul>
                        <div className="navbar__theme-toggle navbar__theme-toggle--mobile">
                            <ThemeToggle />
                        </div>
                    </div>
                )}
            </div>
        </nav>
    );
};

Header.propTypes = {
    isScrolled: PropTypes.bool.isRequired,
};

export default Header;